import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiRefreshCw,
  FiAlertTriangle,
  FiInbox,
  FiDollarSign,
  FiTrendingUp,
  FiCalendar,
  FiZap,
} from 'react-icons/fi';
import Layout from '../components/Layout';
import theme from '../styles/theme';
import { contratosAPI } from '../services/api';
import toast from 'react-hot-toast';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const DashboardGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const ChartCard = styled(Card)`
  min-height: 300px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 ${theme.spacing[4]} 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const ChartContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[6]};
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const LegendLine = styled.div`
  width: 30px;
  height: 2px;
  background: ${(props) => props.$color};
  ${(props) => props.$dashed && 'background: repeating-linear-gradient(to right, ' + props.$color + ' 0, ' + props.$color + ' 5px, transparent 5px, transparent 10px);'}
`;

const Tooltip = styled.div`
  position: absolute;
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  pointer-events: none;
  z-index: 1000;
  box-shadow: ${theme.shadows.md};
  white-space: nowrap;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SummaryGrid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FaturasPendentesCard = styled(SummaryCard)`
  background: ${({ $variant }) => {
    if ($variant === 'danger') return theme.colors.error[100] || '#fee2e2';
    if ($variant === 'warning') return theme.colors.warning[100] || '#fef3c7';
    return theme.colors.background.secondary;
  }};
  border-color: ${({ $variant }) => {
    if ($variant === 'danger') return theme.colors.error[500] || '#ef4444';
    if ($variant === 'warning') return theme.colors.warning[500] || '#f59e0b';
    return theme.colors.border.primary;
  }};
  border-width: ${({ $variant }) => ($variant === 'danger' || $variant === 'warning') ? '2px' : '1px'};
`;

const FaturasPendentesMessage = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.xs};
  color: ${({ $variant }) => {
    if ($variant === 'danger') return theme.colors.error[700] || '#b91c1c';
    if ($variant === 'warning') return theme.colors.warning[700] || '#92400e';
    return theme.colors.text.muted;
  }};
  font-weight: 600;
  line-height: 1.4;
  margin-top: ${theme.spacing[1]};
`;

const FaturasPendentesCTA = styled.button`
  margin-top: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.primary[500]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: ${theme.colors.primary[600]};
    border-color: ${theme.colors.primary[600]};
  }
`;

const SummaryLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
  font-weight: 600;
`;

const SummaryValue = styled.span`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const FaturasPendentesLabel = styled(SummaryLabel)`
  color: ${({ $variant }) => {
    if ($variant === 'danger') return theme.colors.error[800] || '#991b1b';
    if ($variant === 'warning') return theme.colors.warning[800] || '#92400e';
    return theme.colors.text.muted;
  }};
  font-weight: 700;
`;

const FaturasPendentesValue = styled(SummaryValue)`
  color: ${({ $variant }) => {
    if ($variant === 'danger') return theme.colors.error[900] || '#7f1d1d';
    if ($variant === 'warning') return theme.colors.warning[900] || '#78350f';
    return theme.colors.text.primary;
  }};
  font-weight: 800;
`;

const PromoCard = styled(SummaryCard)`
  background: linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[700]} 100%);
  border: 1px solid ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(2, 132, 199, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100px;
    height: 100px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(30px, -30px);
  }
`;

const PromoTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  position: relative;
  z-index: 1;
`;

const PromoSubtitle = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  opacity: 0.9;
  line-height: 1.5;
  position: relative;
  z-index: 1;
`;

const PromoButton = styled.button`
  margin-top: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${theme.colors.text.primary};
  background: transparent;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
  align-self: flex-start;

  &:hover {
    background: ${theme.colors.text.primary};
    color: ${theme.colors.primary[600]};
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  max-width: 100%;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.background.muted};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border.primary};
    border-radius: ${theme.borderRadius.full};
    
    &:hover {
      background: ${theme.colors.text.muted};
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 500px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    min-width: 400px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const Td = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[10]} ${theme.spacing[6]};
  text-align: center;
  border: 1px dashed ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  color: ${theme.colors.text.muted};
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  opacity: 0.5;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.error[500]};
  background: ${theme.colors.error[100]};
  color: ${theme.colors.error[700]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 700;
  text-transform: uppercase;
  background: ${({ $status }) => {
    const normalized = ($status || '').toUpperCase();
    if (normalized === 'CONFIRMADO' || normalized === 'RECEIVED' || normalized === 'CONFIRMED') {
      return theme.colors.success[100];
    }
    if (normalized === 'OVERDUE') return theme.colors.error[100];
    return theme.colors.warning[100];
  }};
  color: ${({ $status }) => {
    const normalized = ($status || '').toUpperCase();
    if (normalized === 'CONFIRMADO' || normalized === 'RECEIVED' || normalized === 'CONFIRMED') {
      return theme.colors.success[700];
    }
    if (normalized === 'OVERDUE') return theme.colors.error[700];
    return theme.colors.warning[700];
  }};
`;

const ActionButtonText = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.primary[500]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.primary[600]};
    border-color: ${theme.colors.primary[600]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FaturasTable = styled(Table)`
  margin-top: ${theme.spacing[4]};
`;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value || 0);

const abrirBoleto = (cobranca) => {
  // Priorizar bank_slip_url, depois invoice_url
  const url = cobranca.bank_slip_url || cobranca.invoice_url;
  if (url) {
    window.open(url, '_blank');
  } else {
    toast.error('Boleto não disponível para esta cobrança');
  }
};

const getCobrancaStatusLabel = (status) => {
  switch ((status || 'PENDING').toUpperCase()) {
    case 'RECEIVED':
    case 'CONFIRMED':
    case 'CONFIRMADO':
      return 'Pago';
    case 'OVERDUE':
      return 'Atrasado';
    default:
      return 'Pendente';
  }
};

// Componente do gráfico simples
const GraficoConsumo = ({ dados }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, consumo: 0, acumulado: 0, date: '' });

  if (!dados || dados.length === 0) {
    return (
      <ChartContainer>
        <span style={{ color: theme.colors.text.muted }}>Sem dados de consumo disponíveis</span>
      </ChartContainer>
    );
  }

  // Calcular consumo acumulado (evolução)
  let acumulado = 0;
  const dadosComAcumulado = dados.map((d) => {
    acumulado += d.consumo;
    return {
      ...d,
      acumulado,
    };
  });

  const maxConsumo = Math.max(...dados.map((d) => d.consumo), 1);
  const minConsumo = Math.min(...dados.map((d) => d.consumo), 0);
  const maxAcumulado = Math.max(...dadosComAcumulado.map((d) => d.acumulado), 1);
  const minAcumulado = Math.min(...dadosComAcumulado.map((d) => d.acumulado), 0);
  
  const width = 800;
  const height = 250;
  const paddingLeft = 60;
  const paddingRight = 60; // Aumentado para o segundo eixo Y
  const paddingTop = 20;
  const paddingBottom = 50;
  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  // Calcular valores para o eixo Y esquerdo (consumo diário)
  const numTicksY = 5;
  const tickValuesY = [];
  const rangeY = maxConsumo - minConsumo || 1;
  for (let i = 0; i <= numTicksY; i++) {
    const value = minConsumo + (rangeY * i) / numTicksY;
    tickValuesY.push(Math.round(value));
  }

  // Calcular valores para o eixo Y direito (consumo acumulado)
  const tickValuesYRight = [];
  const rangeYRight = maxAcumulado - minAcumulado || 1;
  for (let i = 0; i <= numTicksY; i++) {
    const value = minAcumulado + (rangeYRight * i) / numTicksY;
    tickValuesYRight.push(Math.round(value));
  }

  // Calcular posições dos pontos para consumo diário
  const pontos = dadosComAcumulado.map((d, index) => {
    const x = paddingLeft + (index / (dados.length - 1 || 1)) * chartWidth;
    const normalizedValue = (d.consumo - minConsumo) / rangeY;
    const y = paddingTop + chartHeight - (normalizedValue * chartHeight);
    return { x, y, consumo: d.consumo, acumulado: d.acumulado, data: d.data };
  });

  // Calcular posições dos pontos para consumo acumulado
  const pontosAcumulado = dadosComAcumulado.map((d, index) => {
    const x = paddingLeft + (index / (dados.length - 1 || 1)) * chartWidth;
    const normalizedValue = (d.acumulado - minAcumulado) / rangeYRight;
    const y = paddingTop + chartHeight - (normalizedValue * chartHeight);
    return { x, y, acumulado: d.acumulado, data: d.data };
  });

  // Limites Y do gráfico (área visível)
  const yMin = paddingTop;
  const yMax = paddingTop + chartHeight;

  // Função para criar linha suave usando curvas de Bézier cúbicas
  // com pontos de controle conservadores que garantem que a curva não ultrapasse os limites
  const criarLinhaSuave = (pontosArray, minY, maxY) => {
    if (pontosArray.length < 2) return '';
    if (pontosArray.length === 2) {
      return `M ${pontosArray[0].x} ${pontosArray[0].y} L ${pontosArray[1].x} ${pontosArray[1].y}`;
    }

    let path = `M ${pontosArray[0].x} ${pontosArray[0].y}`;
    
    for (let i = 0; i < pontosArray.length - 1; i++) {
      const p0 = pontosArray[i - 1] || pontosArray[i];
      const p1 = pontosArray[i];
      const p2 = pontosArray[i + 1];
      const p3 = pontosArray[i + 2] || pontosArray[i + 1];
      
      // Calcular pontos de controle com uma tensão menor para mais controle
      const tension = 0.25;
      
      // Calcular direções baseadas nos pontos adjacentes
      const dx1 = p2.x - p0.x;
      const dy1 = p2.y - p0.y;
      const dx2 = p3.x - p1.x;
      const dy2 = p3.y - p1.y;
      
      // Calcular pontos de controle iniciais
      const cp1x = p1.x + tension * dx1;
      let cp1y = p1.y + tension * dy1;
      const cp2x = p2.x - tension * dx2;
      let cp2y = p2.y - tension * dy2;
      
      // Encontrar envelope dos valores Y entre p1 e p2
      const minYEnvelope = Math.min(p1.y, p2.y);
      const maxYEnvelope = Math.max(p1.y, p2.y);
      
      // Calcular limites seguros que respeitam tanto o envelope quanto os limites do gráfico
      const safeMinY = Math.max(minY, minYEnvelope);
      const safeMaxY = Math.min(maxY, maxYEnvelope);
      
      // Limitar pontos de controle para ficarem muito próximos dos valores dos pontos
      // Isso garante que a curva não ultrapasse os limites
      cp1y = Math.max(safeMinY, Math.min(safeMaxY, cp1y));
      cp2y = Math.max(safeMinY, Math.min(safeMaxY, cp2y));
      
      // Aplicar uma correção adicional: mover os pontos de controle ainda mais próximos dos pontos
      // Isso reduz ainda mais a chance de ultrapassar os limites
      const proximityFactor = 0.7; // Mais próximo = mais seguro
      cp1y = cp1y * proximityFactor + p1.y * (1 - proximityFactor);
      cp2y = cp2y * proximityFactor + p2.y * (1 - proximityFactor);
      
      // Garantir que ainda estão dentro dos limites seguros
      cp1y = Math.max(safeMinY, Math.min(safeMaxY, cp1y));
      cp2y = Math.max(safeMinY, Math.min(safeMaxY, cp2y));
      
      // Garantir limites absolutos do gráfico
      cp1y = Math.max(minY, Math.min(maxY, cp1y));
      cp2y = Math.max(minY, Math.min(maxY, cp2y));
      
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    
    return path;
  };

  const pathData = criarLinhaSuave(pontos, yMin, yMax);
  const pathDataAcumulado = criarLinhaSuave(pontosAcumulado, yMin, yMax);

  // Calcular posições para labels do eixo X (mostrar algumas datas)
  const numTicksX = 6;
  const tickIndices = [];
  for (let i = 0; i < numTicksX; i++) {
    const index = Math.floor((i / (numTicksX - 1)) * (dados.length - 1));
    tickIndices.push(index);
  }

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    // Se a data vem como string no formato YYYY-MM-DD, processar como data local
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [ano, mes, dia] = dateStr.split('-').map(Number);
      return `${dia}/${mes}`;
    }
    // Caso contrário, processar como Date normal
    const date = new Date(dateStr);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return '';
    // Se a data vem como string no formato YYYY-MM-DD, processar como data local
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [ano, mes, dia] = dateStr.split('-').map(Number);
      // Criar data local (não UTC) para evitar problemas de timezone
      const date = new Date(ano, mes - 1, dia);
      return date.toLocaleDateString('pt-BR');
    }
    // Caso contrário, processar como Date normal
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  const handleMouseMove = (e, ponto, index) => {
    const svg = e.currentTarget.closest('svg');
    if (!svg) return;
    
    const rect = svg.getBoundingClientRect();
    
    setTooltip({
      visible: true,
      x: e.clientX - rect.left + 10,
      y: e.clientY - rect.top - 10,
      consumo: ponto.consumo,
      acumulado: ponto.acumulado,
      date: formatDateFull(ponto.data),
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, x: 0, y: 0, consumo: 0, acumulado: 0, date: '' });
  };

  return (
    <ChartContainer>
      <svg 
        width="100%" 
        height="250px" 
        viewBox={`0 0 ${width} ${height}`} 
        style={{ maxHeight: '300px' }}
        onMouseLeave={handleMouseLeave}
      >
        {/* Grid lines horizontais */}
        {tickValuesY.map((value, index) => {
          const ratio = index / (tickValuesY.length - 1);
          const y = paddingTop + chartHeight - (ratio * chartHeight);
          return (
            <g key={`left-${index}-${value}`}>
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke={theme.colors.border.primary}
                strokeWidth="1"
                opacity="0.3"
              />
              {/* Label do eixo Y esquerdo (consumo diário) */}
              <text
                x={paddingLeft - 10}
                y={y + 4}
                fill={theme.colors.text.muted}
                fontSize="11"
                textAnchor="end"
                fontFamily="system-ui"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Labels do eixo Y direito (consumo acumulado) */}
        {tickValuesYRight.map((value, index) => {
          const ratio = index / (tickValuesYRight.length - 1);
          const y = paddingTop + chartHeight - (ratio * chartHeight);
          return (
            <g key={`right-${index}-${value}`}>
              <text
                x={width - paddingRight + 10}
                y={y + 4}
                fill={theme.colors.text.muted}
                fontSize="11"
                textAnchor="start"
                fontFamily="system-ui"
                opacity="0.7"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* Linha do gráfico - Consumo diário */}
        <path
          d={pathData}
          fill="none"
          stroke={theme.colors.primary[500]}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Linha do gráfico - Consumo acumulado */}
        <path
          d={pathDataAcumulado}
          fill="none"
          stroke={theme.colors.success[500] || '#22c55e'}
          strokeWidth="2"
          strokeDasharray="5,5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.8"
        />
        
        {/* Áreas invisíveis para interatividade do tooltip - Consumo diário */}
        {pontos.map((p, i) => (
            <circle
            key={`daily-interactive-${i}`}
              cx={p.x}
              cy={p.y}
            r="8"
            fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseMove={(e) => handleMouseMove(e, p, i)}
            />
        ))}

        {/* Áreas invisíveis para interatividade do tooltip - Consumo acumulado */}
        {pontosAcumulado.map((p, i) => (
            <circle
            key={`accumulated-interactive-${i}`}
              cx={p.x}
              cy={p.y}
            r="8"
            fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseMove={(e) => handleMouseMove(e, pontos[i], i)}
            />
        ))}

        {/* Eixo X - Labels de datas */}
        {tickIndices.map((index) => {
          if (index >= dados.length) return null;
          const ponto = pontos[index];
          // Usar a data diretamente do ponto, que já contém a data correta
          return (
              <text
              key={index}
                x={ponto.x}
                y={height - paddingBottom + 20}
                fill={theme.colors.text.muted}
                fontSize="10"
                textAnchor="middle"
                fontFamily="system-ui"
              >
              {formatDateLabel(ponto.data)}
              </text>
          );
        })}

        {/* Linha do eixo X */}
        <line
          x1={paddingLeft}
          y1={paddingTop + chartHeight}
          x2={width - paddingRight}
          y2={paddingTop + chartHeight}
          stroke={theme.colors.border.primary}
          strokeWidth="1"
        />

        {/* Linha do eixo Y esquerdo */}
        <line
          x1={paddingLeft}
          y1={paddingTop}
          x2={paddingLeft}
          y2={paddingTop + chartHeight}
          stroke={theme.colors.border.primary}
          strokeWidth="1"
        />

        {/* Linha do eixo Y direito */}
        <line
          x1={width - paddingRight}
          y1={paddingTop}
          x2={width - paddingRight}
          y2={paddingTop + chartHeight}
          stroke={theme.colors.border.primary}
          strokeWidth="1"
        />
      </svg>
      
      {/* Tooltip */}
      <Tooltip
        $visible={tooltip.visible}
        style={{
          left: `${tooltip.x}px`,
          top: `${tooltip.y}px`,
          transform: 'translate(-50%, -100%)',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{tooltip.date}</div>
        <div style={{ marginBottom: '2px', color: theme.colors.primary[500] }}>
          Consumo: {tooltip.consumo} créditos
        </div>
        <div style={{ color: theme.colors.success[500] || '#22c55e' }}>
          Acumulado: {tooltip.acumulado} créditos
        </div>
      </Tooltip>
      
      {/* Legenda */}
      <ChartLegend>
        <LegendItem>
          <LegendLine $color={theme.colors.primary[500]} />
          <span>Consumo Diário</span>
        </LegendItem>
        <LegendItem>
          <LegendLine $color={theme.colors.success[500] || '#22c55e'} $dashed />
          <span>Consumo Acumulado</span>
        </LegendItem>
      </ChartLegend>
    </ChartContainer>
  );
};

const DashboardCreditos = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [dados, setDados] = useState(null);
  const [clienteSelecionado, setClienteSelecionado] = useState('');
  const [cicloAtual, setCicloAtual] = useState(null);
  const [dadosConsumo, setDadosConsumo] = useState([]);
  const [periodoSelecionado, setPeriodoSelecionado] = useState('ciclo_atual');
  
  // Refs para rastrear último carregamento e evitar chamadas duplicadas
  const ultimoCarregamentoRef = useRef({ clienteId: null, periodo: null, timestamp: 0 });
  const ultimoCarregamentoDadosRef = useRef({ timestamp: 0 });
  const carregandoRef = useRef(false);
  const carregandoDadosRef = useRef(false);
  const timeoutRef = useRef(null);
  const timeoutDadosRef = useRef(null);

  // Função específica para buscar dados do gráfico (ciclo e consumo)
  const carregarDadosGrafico = useCallback(async (clienteIdParam = null, periodoParam = null) => {
    const clienteId = clienteIdParam || clienteSelecionado || (dados?.clientes && dados.clientes.length > 0 ? dados.clientes[0].id : null);
    const periodo = periodoParam || periodoSelecionado;

    if (!clienteId) {
      setCicloAtual(null);
      setDadosConsumo([]);
      return;
    }

    const agora = Date.now();
    const ultimoCarregamento = ultimoCarregamentoRef.current;
    
    // Evitar chamadas duplicadas para o mesmo clienteId e período
    // Verifica se já foi carregado recentemente (últimos 500ms) ou se está carregando
    if (ultimoCarregamento.clienteId === clienteId && 
        ultimoCarregamento.periodo === periodo &&
        (carregandoRef.current || (agora - ultimoCarregamento.timestamp < 500))) {
      return;
    }

    // Limpar timeout anterior se existir
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    carregandoRef.current = true;
    ultimoCarregamentoRef.current = { clienteId, periodo, timestamp: agora };

    try {
      const [cicloResponse, consumoResponse] = await Promise.all([
        contratosAPI.buscarContratoAtivoPorCliente(clienteId),
        contratosAPI.buscarConsumoPorCliente(clienteId, periodo),
      ]);
      
      if (cicloResponse.data?.success && cicloResponse.data.data?.ciclo_atual) {
        setCicloAtual(cicloResponse.data.data.ciclo_atual);
      } else {
        setCicloAtual(null);
      }

      if (consumoResponse.data?.success && Array.isArray(consumoResponse.data.data)) {
        setDadosConsumo(consumoResponse.data.data);
      } else {
        setDadosConsumo([]);
      }
    } catch (error) {
      console.error('Erro ao buscar ciclo atual ou consumo', error);
      setCicloAtual(null);
      setDadosConsumo([]);
    } finally {
      carregandoRef.current = false;
    }
  }, [clienteSelecionado, periodoSelecionado, dados]);

  const carregarDados = useCallback(async (silent = false, clienteId = null) => {
    const agora = Date.now();
    const ultimoCarregamento = ultimoCarregamentoDadosRef.current;
    
    // Evitar chamadas duplicadas muito próximas (útil para StrictMode)
    if (carregandoDadosRef.current || (agora - ultimoCarregamento.timestamp < 500)) {
      return;
    }

    carregandoDadosRef.current = true;
    ultimoCarregamentoDadosRef.current = { timestamp: agora };

    try {
      if (!silent) {
        setLoading(true);
      }
      setErro(null);
      const response = await contratosAPI.dashboardMeusContratos(clienteId);
      if (response.data?.success) {
        setDados(response.data.data);
      } else {
        setErro('Não foi possível carregar os dados.');
      }

      // Não chamar carregarDadosGrafico aqui - o useEffect cuidará disso quando dados mudar
      // Isso evita chamadas duplicadas
    } catch (error) {
      console.error('Erro ao carregar dashboard de créditos', error);
      setErro('Erro ao carregar dashboard de créditos.');
    } finally {
      carregandoDadosRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Limpar timeout anterior se existir
    if (timeoutDadosRef.current) {
      clearTimeout(timeoutDadosRef.current);
    }

    // Usar debounce para evitar chamadas duplicadas do StrictMode
    timeoutDadosRef.current = setTimeout(() => {
      carregarDados();
    }, 50);

    return () => {
      if (timeoutDadosRef.current) {
        clearTimeout(timeoutDadosRef.current);
        timeoutDadosRef.current = null;
      }
    };
  }, [carregarDados]);

  // useEffect otimizado para evitar chamadas duplicadas
  // Usa debounce e ref para prevenir chamadas duplicadas (incluindo StrictMode)
  useEffect(() => {
    if (!dados || !dados.clientes || dados.clientes.length === 0) {
      return;
    }

    // Limpar timeout anterior se existir
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Usar debounce para evitar chamadas muito próximas (útil para StrictMode)
    timeoutRef.current = setTimeout(() => {
      const clienteId = clienteSelecionado || (dados?.clientes && dados.clientes.length > 0 ? dados.clientes[0].id : null);
      const periodo = periodoSelecionado;
      
      // Verificar novamente antes de chamar (pode ter mudado durante o debounce)
      const ultimoCarregamento = ultimoCarregamentoRef.current;
      if (ultimoCarregamento.clienteId === clienteId && 
          ultimoCarregamento.periodo === periodo &&
          (Date.now() - ultimoCarregamento.timestamp < 500)) {
        return;
      }
      
      carregarDadosGrafico();
    }, 100); // Debounce de 100ms

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [dados, clienteSelecionado, periodoSelecionado, carregarDadosGrafico]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        carregarDados(true, clienteSelecionado || null);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [clienteSelecionado]);

  const clientesLista = dados?.clientes || [];
  const cobrancasCiclo = dados?.cobrancasCiclo || [];
  const cobrancasExtensao = dados?.cobrancasExtensao || [];
  const totais = dados?.totais || { creditos_saldo: 0 };

  // Calcular dados do dashboard
  const dadosDashboard = useMemo(() => {
    // Marcar tipo de cobrança antes de combinar
    const cobrancasCicloComTipo = cobrancasCiclo.map((c) => ({
      ...c,
      tipo: 'ciclo',
    }));
    
    const cobrancasExtensaoComTipo = cobrancasExtensao.map((c) => ({
      ...c,
      tipo: 'extensao',
    }));
    
    const todasCobrancas = [...cobrancasCicloComTipo, ...cobrancasExtensaoComTipo];
    
    // Faturas pendentes
    const faturasPendentes = todasCobrancas.filter(
      (c) => (c.status || '').toUpperCase() === 'PENDING' || (c.status || '').toUpperCase() === 'OVERDUE'
    ).length;

    // Data de início do próximo ciclo
    // Usar o ciclo atual do cliente selecionado
    let proximoCicloInicio = null;
    if (cicloAtual && cicloAtual.fim) {
      const dataFimCicloAtual = new Date(cicloAtual.fim);
      if (!isNaN(dataFimCicloAtual.getTime())) {
        // Calcular início do próximo ciclo (data final do ciclo atual + 1 dia)
        proximoCicloInicio = new Date(dataFimCicloAtual);
        proximoCicloInicio.setDate(dataFimCicloAtual.getDate() + 1);
        proximoCicloInicio.setHours(0, 0, 0, 0);
      }
    }

    // Saldo de crédito do ciclo atual
    const saldoCredito = cicloAtual?.creditos_saldo || 0;
    
    // Total de créditos do ciclo atual
    const totalCreditos = cicloAtual?.creditos_totais || 0;
    
    // Créditos usados do ciclo atual
    const creditosUsados = cicloAtual?.creditos_usados || 0;

    // Dados do gráfico (consumo de créditos nos últimos 30 dias)
    // Usar dados reais de consumo se disponíveis, caso contrário usar array vazio
    const dadosGrafico = dadosConsumo.length > 0 
      ? dadosConsumo 
      : (() => {
          // Fallback: criar array vazio para os últimos 30 dias se não houver dados
          const hoje = new Date();
          const dadosVazios = [];
          for (let i = 29; i >= 0; i--) {
            const data = new Date(hoje);
            data.setDate(hoje.getDate() - i);
            const dataStr = data.toISOString().split('T')[0];
            dadosVazios.push({
              data: dataStr,
              consumo: 0,
            });
          }
          return dadosVazios;
        })();

    // Função para obter prioridade do status (menor número = maior prioridade)
    const getStatusPriority = (status) => {
      const normalized = (status || 'PENDING').toUpperCase();
      if (normalized === 'OVERDUE') return 1; // Atrasados primeiro
      if (normalized === 'PENDING') return 2; // Pendentes segundo
      return 3; // Pagos por último
    };

    // Função para obter prioridade do tipo (menor número = maior prioridade)
    const getTipoPriority = (tipo) => {
      if (tipo === 'ciclo') return 1; // Ciclos primeiro
      if (tipo === 'extensao') return 2; // Extensões depois
      return 3;
    };

    // Filtrar apenas faturas não pagas para exibição no dashboard
    const faturasNaoPagas = todasCobrancas.filter((c) => {
      const statusNormalizado = (c.status || 'PENDING').toUpperCase();
      return statusNormalizado !== 'RECEIVED' && statusNormalizado !== 'CONFIRMED' && statusNormalizado !== 'CONFIRMADO';
    });

    // Lógica para determinar o estado do card "Faturas Pendentes"
    const faturasCicloAtrasadas = cobrancasCicloComTipo.filter((c) => {
      const status = (c.status || '').toUpperCase();
      return status === 'OVERDUE';
    });

    const faturasExtensaoAtrasadas = cobrancasExtensaoComTipo.filter((c) => {
      const status = (c.status || '').toUpperCase();
      return status === 'OVERDUE';
    });

    const faturasExtensaoPendentes = cobrancasExtensaoComTipo.filter((c) => {
      const status = (c.status || '').toUpperCase();
      return status === 'PENDING' || status === 'OVERDUE';
    });

    // Calcular data limite de bloqueio (due_date + 5 dias)
    const calcularDataLimiteBloqueio = () => {
      if (faturasCicloAtrasadas.length === 0) return null;
      
      // Encontrar a fatura mais antiga em atraso (do ciclo atual)
      const faturaMaisAntiga = faturasCicloAtrasadas.reduce((maisAntiga, atual) => {
        if (!maisAntiga) return atual;
        const dataAtual = new Date(atual.due_date);
        dataAtual.setHours(0, 0, 0, 0);
        const dataMaisAntiga = new Date(maisAntiga.due_date);
        dataMaisAntiga.setHours(0, 0, 0, 0);
        
        return dataAtual < dataMaisAntiga ? atual : maisAntiga;
      }, null);

      if (!faturaMaisAntiga || !faturaMaisAntiga.due_date) return null;

      // Pegar a due_date e adicionar 5 dias
      const dataVencimento = new Date(faturaMaisAntiga.due_date);
      dataVencimento.setHours(0, 0, 0, 0);
      
      // Adicionar 5 dias à data de vencimento
      const dataLimiteBloqueio = new Date(dataVencimento);
      dataLimiteBloqueio.setDate(dataVencimento.getDate() + 5);
      
      return dataLimiteBloqueio;
    };

    // Determinar estado do card de faturas pendentes
    let estadoFaturasPendentes = {
      variant: 'default', // 'default', 'danger', 'warning'
      mensagem: null,
      mostrarCTA: false,
      ctaTexto: null,
      ctaAcao: null,
    };

    // Prioridade 1: Ciclo do Contrato em atraso
    if (faturasCicloAtrasadas.length > 0) {
      const dataLimiteBloqueio = calcularDataLimiteBloqueio();
      let mensagem = 'A fatura de Ciclo do Contrato não paga dentro do prazo vai acarretar na paralisação das mensagens do WhatsApp. Realize o pagamento imediatamente para evitar a interrupção.';
      
      if (dataLimiteBloqueio) {
        const dataFormatada = formatDate(dataLimiteBloqueio);
        mensagem = `A fatura de Ciclo do Contrato não paga dentro do prazo vai acarretar na paralisação das mensagens do WhatsApp em ${dataFormatada}. Realize o pagamento para evitar a interrupção.`;
      }
      
      estadoFaturasPendentes = {
        variant: 'danger',
        mensagem,
        mostrarCTA: false,
        ctaTexto: null,
        ctaAcao: null,
      };
    }
    // Prioridade 2: Extensão do Contrato em atraso (só se não houver ciclo em atraso)
    else if (faturasExtensaoAtrasadas.length > 0 || faturasExtensaoPendentes.length > 0) {
      // Se ainda há créditos disponíveis
      if (saldoCredito > 0) {
        estadoFaturasPendentes = {
          variant: 'warning',
          mensagem: 'Existem faturas de extensões de crédito em atraso.',
          mostrarCTA: false,
          ctaTexto: null,
          ctaAcao: null,
        };
      }
      // Se não há créditos e há extensões não pagas
      else {
        estadoFaturasPendentes = {
          variant: 'danger',
          mensagem: 'As mensagens do WhatsApp foram paralisadas. Realize o pagamento da extensão de crédito para reativar o envio de mensagens.',
          mostrarCTA: false,
          ctaTexto: null,
          ctaAcao: null,
        };
      }
    }
    // Prioridade 3: Não há faturas em atraso, mas não há créditos e não há extensões não pagas
    // Verificar se os créditos utilizados ultrapassaram 80% do total
    else if (saldoCredito === 0 && faturasExtensaoPendentes.length === 0) {
      // Calcular se créditos usados > 80% do total
      const percentualUsado = totalCreditos > 0 ? (creditosUsados / totalCreditos) * 100 : 0;
      const ultrapassou80Porcento = percentualUsado > 80;
      
      if (ultrapassou80Porcento) {
        estadoFaturasPendentes = {
          variant: 'default',
          mensagem: null,
          mostrarCTA: true,
          ctaTexto: 'Compre créditos',
          ctaAcao: null, // Será passado via closure
        };
      }
    }

    return {
      faturasPendentes,
      proximoCicloInicio,
      saldoCredito,
      totalCreditos,
      creditosUsados,
      dadosGrafico,
      estadoFaturasPendentes,
      todasCobrancas: faturasNaoPagas.sort((a, b) => {
        // Primeiro ordenar por tipo (ciclos antes de extensões)
        const tipoPriorityA = getTipoPriority(a.tipo);
        const tipoPriorityB = getTipoPriority(b.tipo);
        
        if (tipoPriorityA !== tipoPriorityB) {
          return tipoPriorityA - tipoPriorityB;
        }
        
        // Depois ordenar por status
        const priorityA = getStatusPriority(a.status);
        const priorityB = getStatusPriority(b.status);
        
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
        
        // Se o status for o mesmo, ordenar por data de vencimento (mais recente primeiro)
        const dateA = a.due_date ? new Date(a.due_date) : new Date(0);
        const dateB = b.due_date ? new Date(b.due_date) : new Date(0);
        return dateB - dateA;
      }),
    };
  }, [cobrancasCiclo, cobrancasExtensao, totais, cicloAtual, dadosConsumo]);

  const mostrarFiltroCliente = clientesLista.length > 1;

  if (loading) {
    return (
      <Layout>
        <Container>
          <Card>
            <Subtitle>Carregando informações...</Subtitle>
          </Card>
        </Container>
      </Layout>
    );
  }

  if (erro) {
    return (
      <Layout>
        <Container>
          <Card>
            <Alert>
              <FiAlertTriangle />
              {erro}
            </Alert>
            <GhostButton onClick={() => carregarDados(false)}>
              <FiRefreshCw />
              Tentar novamente
            </GhostButton>
          </Card>
        </Container>
      </Layout>
    );
  }

  const handleVerDiferenciais = () => {
    // Se houver mais de um cliente e nenhum cliente selecionado
    if (clientesLista.length > 1 && !clienteSelecionado) {
      toast.error('Selecione um cliente no filtro acima para ver os diferenciais do plano');
      return;
    }

    // Redirecionar para upgrade com cliente_uuid se houver seleção
    if (clienteSelecionado) {
      const clienteSelecionadoObj = clientesLista.find(c => c.id === parseInt(clienteSelecionado, 10));
      if (clienteSelecionadoObj && clienteSelecionadoObj.uuid) {
        navigate(`/upgrade-plano?cliente_uuid=${clienteSelecionadoObj.uuid}`);
      } else {
        navigate('/upgrade-plano');
      }
    } else {
      navigate('/upgrade-plano');
    }
  };

  return (
    <Layout>
      <Container>
        {mostrarFiltroCliente && (
          <Header>
            <HeaderGroup>
              <Title>Dashboard de Créditos</Title>
              <Subtitle>Consulte seu consumo de créditos e faturas.</Subtitle>
            </HeaderGroup>
            <ActionBar>
              <Select
                value={clienteSelecionado}
                onChange={(e) => setClienteSelecionado(e.target.value)}
                style={{ minWidth: '200px' }}
              >
                <option value="">Todos os clientes</option>
                {clientesLista.map((cliente) => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nome}
                  </option>
                ))}
              </Select>
              <GhostButton onClick={() => carregarDados(false, clienteSelecionado || null)}>
                <FiRefreshCw />
                Atualizar
              </GhostButton>
            </ActionBar>
          </Header>
        )}

        <DashboardGrid>
          <ChartCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing[4], flexWrap: 'wrap', gap: theme.spacing[3] }}>
              <ChartTitle style={{ margin: 0 }}>
                <FiTrendingUp />
                Consumo de créditos
              </ChartTitle>
              <Select
                value={periodoSelecionado}
                onChange={(e) => {
                  const novoPeriodo = e.target.value;
                  setPeriodoSelecionado(novoPeriodo);
                  // Atualizar gráfico imediatamente ao selecionar o período
                  carregarDadosGrafico(null, novoPeriodo);
                }}
                style={{ minWidth: '180px' }}
              >
                <option value="ciclo_atual">Ciclo Atual</option>
                <option value="30">Últimos 30 dias</option>
                <option value="60">Últimos 60 dias</option>
                <option value="90">Últimos 90 dias</option>
              </Select>
            </div>
            <GraficoConsumo dados={dadosDashboard.dadosGrafico} />
          </ChartCard>

          <SummaryGrid2>
            <SummaryCard>
              <SummaryLabel>Total Créditos</SummaryLabel>
              <SummaryValue>{dadosDashboard.totalCreditos.toLocaleString('pt-BR')}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <SummaryLabel>Créditos Usados</SummaryLabel>
              <SummaryValue>{dadosDashboard.creditosUsados.toLocaleString('pt-BR')}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <SummaryLabel>Saldo de Crédito</SummaryLabel>
              <SummaryValue>{dadosDashboard.saldoCredito.toLocaleString('pt-BR')}</SummaryValue>
            </SummaryCard>
          </SummaryGrid2>

          <SummaryGrid>
            <FaturasPendentesCard $variant={dadosDashboard.estadoFaturasPendentes.variant}>
              <FaturasPendentesLabel $variant={dadosDashboard.estadoFaturasPendentes.variant}>
                Faturas Pendentes
              </FaturasPendentesLabel>
              <FaturasPendentesValue $variant={dadosDashboard.estadoFaturasPendentes.variant}>
                {dadosDashboard.faturasPendentes}
              </FaturasPendentesValue>
              {dadosDashboard.estadoFaturasPendentes.mensagem && (
                <FaturasPendentesMessage $variant={dadosDashboard.estadoFaturasPendentes.variant}>
                  {dadosDashboard.estadoFaturasPendentes.mensagem}
                </FaturasPendentesMessage>
              )}
              {dadosDashboard.estadoFaturasPendentes.mostrarCTA && dadosDashboard.estadoFaturasPendentes.ctaTexto && (
                <FaturasPendentesCTA
                  onClick={() => navigate('/comprar-creditos')}
                >
                  {dadosDashboard.estadoFaturasPendentes.ctaTexto}
                </FaturasPendentesCTA>
              )}
            </FaturasPendentesCard>
            <SummaryCard>
              <SummaryLabel>Próximo Ciclo</SummaryLabel>
              <SummaryValue>
                {dadosDashboard.proximoCicloInicio
                  ? formatDate(dadosDashboard.proximoCicloInicio)
                  : '—'}
              </SummaryValue>
            </SummaryCard>
            <PromoCard 
              onClick={handleVerDiferenciais}
              style={{ cursor: 'pointer' }}
            >
              <PromoTitle>💼 HAEVO Premium</PromoTitle>
              <PromoSubtitle>Desbloqueie relatórios de demanda, estoque e oportunidades de venda.</PromoSubtitle>
              <PromoButton>Ver diferenciais</PromoButton>
            </PromoCard>
          </SummaryGrid>
        </DashboardGrid>

        {dadosDashboard.todasCobrancas.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>
                <FiDollarSign />
                Faturas
              </CardTitle>
            </CardHeader>
            <TableWrapper>
              <FaturasTable>
                <thead>
                  <tr>
                    <Th>Tipo</Th>
                    <Th>Vencimento</Th>
                    <Th>Valor</Th>
                    <Th>Status</Th>
                    <Th>Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {dadosDashboard.todasCobrancas.slice(0, 10).map((cobranca) => {
                    const statusNormalizado = (cobranca.status || 'PENDING').toUpperCase();
                    const isPendente = statusNormalizado === 'PENDING' || statusNormalizado === 'OVERDUE';
                    const isPago = statusNormalizado === 'RECEIVED' || statusNormalizado === 'CONFIRMED' || statusNormalizado === 'CONFIRMADO';

                    return (
                      <tr key={cobranca.id}>
                        <Td>
                          {cobranca.tipo === 'ciclo' ? 'Ciclo do Contrato' : 'Extensão do Contrato'}
                        </Td>
                        <Td>{formatDate(cobranca.due_date)}</Td>
                        <Td>{formatCurrency(cobranca.valor)}</Td>
                        <Td>
                          <StatusBadge $status={cobranca.status}>
                            {getCobrancaStatusLabel(cobranca.status)}
                          </StatusBadge>
                        </Td>
                        <Td>
                          {isPendente ? (
                            <ActionButtonText
                              onClick={() => {
                                abrirBoleto(cobranca);
                              }}
                              disabled={!cobranca.bank_slip_url && !cobranca.invoice_url}
                            >
                              Pagar agora
                            </ActionButtonText>
                          ) : null}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </FaturasTable>
            </TableWrapper>
          </Card>
        )}
      </Container>
    </Layout>
  );
};

export default DashboardCreditos;

